export const reportLineMappingsEn = {
  "reportLines.title": "Custom report lines",
  "reportLines.description": "Group posted accounts into your own report lines. Drafts and previews are for internal use.",
  "reportLines.scope": "Select a company, project and dated period to view custom report lines.",
  "reportLines.readOnly": "You can view mappings, but only project editors can create or approve them.",
  "reportLines.versions": "Mapping versions",
  "reportLines.automatic": "Effective approved version for this period",
  "reportLines.none": "No mapping versions yet.",
  "reportLines.draft": "Draft — not approved",
  "reportLines.approved": "Approved",
  "reportLines.new": "Create new draft",
  "reportLines.copy": "Create new draft from this version",
  "reportLines.editor": "New mapping version",
  "reportLines.effective": "Effective date",
  "reportLines.dateHint": "Choose a new effective date. Saved versions cannot be changed.",
  "reportLines.include": "Include",
  "reportLines.code": "Line code",
  "reportLines.label": "Line label",
  "reportLines.account": "Account",
  "reportLines.save": "Save draft",
  "reportLines.cancel": "Cancel",
  "reportLines.validation": "Set an effective date and a code and label for every included account. Use the same label for accounts sharing a line code.",
  "reportLines.dateConflict": "A version already has this effective date. Choose another date.",
  "reportLines.saveError": "Could not save the draft. Check the accounts and try again.",
  "reportLines.approveError": "Could not approve this mapping. Refresh its preview and try again.",
  "reportLines.loadError": "Could not load custom report lines. Try again.",
  "reportLines.preview": "Preview for selected period",
  "reportLines.refresh": "Refresh preview",
  "reportLines.noMapping": "No approved mapping is effective for this period. Select a draft or create one to preview it.",
  "reportLines.historical": "This approved version is historical for the selected period. Choose the effective version to preview it.",
  "reportLines.future": "This mapping takes effect after the selected period. Choose another period to preview it.",
  "reportLines.draftPreview": "Draft preview — not approved",
  "reportLines.ready": "Approved mapping with complete coverage and reconciled totals",
  "reportLines.notReady": "Preview needs review: coverage or reconciliation is incomplete.",
  "reportLines.outdated": "Books or mapping changed. Refresh the preview before approval.",
  "reportLines.approve": "Approve this draft",
  "reportLines.approvalHint": "Approval requires a current preview with all posted accounts mapped, compatible accounts and reconciled totals.",
  "reportLines.unmapped": "Unmapped posted accounts",
  "reportLines.incompatible": "Incompatible accounts",
  "reportLines.coverage": "Mapped posted accounts",
  "reportLines.reconciliation": "Section reconciliation (EUR)",
  "reportLines.mapped": "Mapped",
  "reportLines.unmappedTotal": "Unmapped",
  "reportLines.bookTotal": "Book total",
  "reportLines.state": "Check",
  "reportLines.ok": "Reconciled",
  "reportLines.difference": "Difference found",
  "reportLines.trial": "Trial balance checks",
  "reportLines.balance": "Balance sheet check",
  "reportLines.lines": "Grouped report lines (EUR)",
  "reportLines.amount": "Amount",
  "reportLines.noLines": "No mapped postings for this period.",
} as const

export const reportLineMappingsEt: Record<keyof typeof reportLineMappingsEn, string> = {
  "reportLines.title": "Kohandatud aruanderead",
  "reportLines.description": "Rühmita kinnitatud kontod oma aruanderidadeks. Mustandid ja eelvaated on sisekasutuseks.",
  "reportLines.scope": "Kohandatud aruanderidade vaatamiseks vali ettevõte, projekt ja kuupäevadega periood.",
  "reportLines.readOnly": "Seadistusi saad vaadata, kuid luua ja kinnitada saavad neid ainult projekti muutmisõigusega kasutajad.",
  "reportLines.versions": "Seadistuse versioonid",
  "reportLines.automatic": "Sellel perioodil kehtiv kinnitatud versioon",
  "reportLines.none": "Seadistuse versioone veel pole.",
  "reportLines.draft": "Mustand – kinnitamata",
  "reportLines.approved": "Kinnitatud",
  "reportLines.new": "Loo uus mustand",
  "reportLines.copy": "Loo selle versiooni põhjal uus mustand",
  "reportLines.editor": "Uus seadistuse versioon",
  "reportLines.effective": "Kehtivuse alguskuupäev",
  "reportLines.dateHint": "Vali uus kehtivuse alguskuupäev. Salvestatud versioone muuta ei saa.",
  "reportLines.include": "Kaasa",
  "reportLines.code": "Rea kood",
  "reportLines.label": "Rea nimetus",
  "reportLines.account": "Konto",
  "reportLines.save": "Salvesta mustand",
  "reportLines.cancel": "Tühista",
  "reportLines.validation": "Määra kuupäev ning igale kaasatud kontole rea kood ja nimetus. Sama koodiga ridadel peab olema sama nimetus.",
  "reportLines.dateConflict": "Selle kuupäevaga versioon on juba olemas. Vali teine kuupäev.",
  "reportLines.saveError": "Mustandi salvestamine ebaõnnestus. Kontrolli kontosid ja proovi uuesti.",
  "reportLines.approveError": "Seadistuse kinnitamine ebaõnnestus. Värskenda eelvaadet ja proovi uuesti.",
  "reportLines.loadError": "Kohandatud aruanderidade laadimine ebaõnnestus. Proovi uuesti.",
  "reportLines.preview": "Valitud perioodi eelvaade",
  "reportLines.refresh": "Värskenda eelvaadet",
  "reportLines.noMapping": "Sellel perioodil ei kehti kinnitatud seadistus. Eelvaateks vali mustand või loo uus.",
  "reportLines.historical": "See kinnitatud versioon on valitud perioodi jaoks varasem. Eelvaateks vali kehtiv versioon.",
  "reportLines.future": "See seadistus hakkab kehtima pärast valitud perioodi. Eelvaateks vali teine periood.",
  "reportLines.draftPreview": "Mustandi eelvaade – kinnitamata",
  "reportLines.ready": "Kinnitatud seadistus: kõik kontod kaetud ja summad klapivad",
  "reportLines.notReady": "Eelvaade vajab kontrolli: kõik kontod pole kaetud või summad ei klapi.",
  "reportLines.outdated": "Kanded või seadistus on muutunud. Enne kinnitamist värskenda eelvaadet.",
  "reportLines.approve": "Kinnita see mustand",
  "reportLines.approvalHint": "Kinnitamiseks peavad värskes eelvaates olema kõik kinnitatud kontod õigesti seotud ja summad klappima.",
  "reportLines.unmapped": "Seostamata kinnitatud kontod",
  "reportLines.incompatible": "Sobimatud kontod",
  "reportLines.coverage": "Seostatud kinnitatud kontod",
  "reportLines.reconciliation": "Jaotiste summade võrdlus (EUR)",
  "reportLines.mapped": "Seostatud",
  "reportLines.unmappedTotal": "Seostamata",
  "reportLines.bookTotal": "Raamatute summa",
  "reportLines.state": "Kontroll",
  "reportLines.ok": "Summad klapivad",
  "reportLines.difference": "Leiti erinevus",
  "reportLines.trial": "Käibeandmiku kontrollid",
  "reportLines.balance": "Bilansi kontroll",
  "reportLines.lines": "Rühmitatud aruanderead (EUR)",
  "reportLines.amount": "Summa",
  "reportLines.noLines": "Selle perioodi seostatud kandeid pole.",
}

export const reportLineMappingsRu: Record<keyof typeof reportLineMappingsEn, string> = {
  "reportLines.title": "Пользовательские строки отчёта",
  "reportLines.description": "Группируйте проведённые счета в собственные строки отчёта. Черновики и предварительные просмотры предназначены для внутреннего использования.",
  "reportLines.scope": "Выберите компанию, проект и период с датами для просмотра пользовательских строк отчёта.",
  "reportLines.readOnly": "Вы можете просматривать настройки, но создавать и утверждать их могут только редакторы проекта.",
  "reportLines.versions": "Версии настройки",
  "reportLines.automatic": "Действующая утверждённая версия для этого периода",
  "reportLines.none": "Версий настройки пока нет.",
  "reportLines.draft": "Черновик — не утверждён",
  "reportLines.approved": "Утверждена",
  "reportLines.new": "Создать новый черновик",
  "reportLines.copy": "Создать черновик на основе этой версии",
  "reportLines.editor": "Новая версия настройки",
  "reportLines.effective": "Дата начала действия",
  "reportLines.dateHint": "Выберите новую дату начала действия. Сохранённые версии изменить нельзя.",
  "reportLines.include": "Включить",
  "reportLines.code": "Код строки",
  "reportLines.label": "Название строки",
  "reportLines.account": "Счёт",
  "reportLines.save": "Сохранить черновик",
  "reportLines.cancel": "Отмена",
  "reportLines.validation": "Укажите дату, код и название строки для каждого включённого счёта. Для одного кода строки используйте одинаковое название.",
  "reportLines.dateConflict": "На эту дату версия уже существует. Выберите другую дату.",
  "reportLines.saveError": "Не удалось сохранить черновик. Проверьте счета и повторите попытку.",
  "reportLines.approveError": "Не удалось утвердить настройку. Обновите предварительный просмотр и повторите попытку.",
  "reportLines.loadError": "Не удалось загрузить пользовательские строки отчёта. Повторите попытку.",
  "reportLines.preview": "Предварительный просмотр за выбранный период",
  "reportLines.refresh": "Обновить предварительный просмотр",
  "reportLines.noMapping": "Для этого периода нет действующей утверждённой настройки. Выберите черновик или создайте новый для просмотра.",
  "reportLines.historical": "Эта утверждённая версия устарела для выбранного периода. Для просмотра выберите действующую версию.",
  "reportLines.future": "Настройка начнёт действовать после выбранного периода. Для просмотра выберите другой период.",
  "reportLines.draftPreview": "Предварительный просмотр черновика — не утверждён",
  "reportLines.ready": "Утверждённая настройка: все счета охвачены, итоги сверены",
  "reportLines.notReady": "Нужна проверка: охват счетов или сверка итогов неполные.",
  "reportLines.outdated": "Проводки или настройка изменились. Обновите просмотр перед утверждением.",
  "reportLines.approve": "Утвердить этот черновик",
  "reportLines.approvalHint": "Для утверждения нужен актуальный просмотр: все проведённые счета сопоставлены, типы совместимы, итоги сверены.",
  "reportLines.unmapped": "Несопоставленные проведённые счета",
  "reportLines.incompatible": "Несовместимые счета",
  "reportLines.coverage": "Сопоставленные проведённые счета",
  "reportLines.reconciliation": "Сверка разделов (EUR)",
  "reportLines.mapped": "Сопоставлено",
  "reportLines.unmappedTotal": "Не сопоставлено",
  "reportLines.bookTotal": "Итого по книгам",
  "reportLines.state": "Проверка",
  "reportLines.ok": "Сверено",
  "reportLines.difference": "Обнаружена разница",
  "reportLines.trial": "Проверки оборотно-сальдовой ведомости",
  "reportLines.balance": "Проверка баланса",
  "reportLines.lines": "Сгруппированные строки отчёта (EUR)",
  "reportLines.amount": "Сумма",
  "reportLines.noLines": "За этот период нет сопоставленных проводок.",
}
